/**
 * SalesPipelineMetricsPage — Phase 33.9 Sales Brain pipeline metrics.
 *
 * Shows funnel stats, conversion rate bars, and pipeline status counts.
 * Polls every 60 s.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

interface MetricsResponse {
  total_pipelines: number;
  running_pipelines: number;
  completed_pipelines: number;
  failed_pipelines: number;
  total_leads_found: number;
  total_leads_qualified: number;
  total_emails_drafted: number;
  total_meetings_booked: number;
  conversion_leads_to_qualified: number;
  conversion_qualified_to_email: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchMetrics(): Promise<MetricsResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/pipeline/metrics`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<MetricsResponse>;
}

function useMetrics() {
  return useQuery({
    queryKey: ['sales', 'pipeline', 'metrics'],
    queryFn: fetchMetrics,
    refetchInterval: 60_000,
  });
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function fmtPct(ratio: number): string {
  return (ratio * 100).toFixed(1) + '%';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div
      style={{
        background: 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 18px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>
        {value}
      </div>
    </div>
  );
}

function ConversionBar({ label, ratio }: { label: string; ratio: number }) {
  const pct = Math.min(ratio * 100, 100);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', letterSpacing: '0.04em' }}>{label}</span>
        <span style={{ fontSize: '9.5pt', fontWeight: 700, color: '#6ee7b7' }}>{fmtPct(ratio)}</span>
      </div>
      <div
        style={{
          width: '100%',
          height: 8,
          background: 'var(--omega-rim, rgba(255,255,255,0.08))',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: '#6ee7b7',
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 12 }}>
      {children}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesPipelineMetricsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useMetrics();

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Sales', to: '/omega/sales' },
          { label: 'Pipeline' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Pipeline Metrics
        </h1>
      </div>

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load metrics.</div>
      )}

      {data && (
        <>
          {/* Funnel stat cards */}
          <section style={{ marginBottom: 28 }}>
            <SectionLabel>Funnel</SectionLabel>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <StatCard label="Leads Found"    value={data.total_leads_found}      color="#7dd3fc" />
              <StatCard label="Qualified"       value={data.total_leads_qualified}  color="#6ee7b7" />
              <StatCard label="Emails Drafted"  value={data.total_emails_drafted}   color="#a78bfa" />
              <StatCard label="Meetings Booked" value={data.total_meetings_booked}  color="#fbbf24" />
            </div>
          </section>

          {/* Conversion rate bars */}
          <section style={{ marginBottom: 28 }}>
            <SectionLabel>Conversion Rates</SectionLabel>
            <div
              style={{
                background: 'var(--omega-surface, rgba(255,255,255,0.04))',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                borderRadius: 6,
                padding: '16px 18px',
                maxWidth: 520,
              }}
            >
              <ConversionBar label="Lead → Qualified"   ratio={data.conversion_leads_to_qualified} />
              <ConversionBar label="Qualified → Email"  ratio={data.conversion_qualified_to_email} />
            </div>
          </section>

          {/* Pipeline status cards */}
          <section>
            <SectionLabel>Pipelines</SectionLabel>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <StatCard label="Total"     value={data.total_pipelines} />
              <StatCard label="Running"   value={data.running_pipelines}   color="#a78bfa" />
              <StatCard label="Completed" value={data.completed_pipelines} color="#34d399" />
              <StatCard label="Failed"    value={data.failed_pipelines}    color="#f87171" />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
